import { useEffect, useState, type FC } from "react";
import { useSelector } from "@legendapp/state/react";
import { filterUsers } from "@/pages/admin/UserManagementPage/filterUsers";
import {
  ExternalUsersStore$,
  loadAllUsers,
  reloadCurrent,
} from "@/stores/externalUsersStore";
import UserFilters from "@/pages/admin/UserManagementPage/UserFilters";
import UserFormModal from "@/pages/admin/UserManagementPage/UserFormModal";
import UserManagementHeader from "@/pages/admin/UserManagementPage/UserManagementHeader";
import UsersTable from "@/pages/admin/UserManagementPage/UsersTable";
import { TEXT } from "@/constants/text";
import type { ExternalUser } from "@/types/externalUser";
import styles from "@/pages/admin/UserManagementPage/UserManagementPage.module.css";

type ModalState = { mode: "add" } | { mode: "edit"; user: ExternalUser } | null;

const UserManagementPage: FC = () => {
  const [search, setSearch] = useState("");
  const [first, setFirst] = useState(0);
  const [modal, setModal] = useState<ModalState>(null);

  const users = useSelector(() => ExternalUsersStore$.users.get());
  const isLoading = useSelector(() => ExternalUsersStore$.isLoading.get());
  const error = useSelector(() => ExternalUsersStore$.error.get());

  // Every user, once, on arrival. The whole population is a single upstream
  // call, so there is nothing to choose before seeing anything — and with the
  // rows in hand, filtering happens locally rather than as further requests.
  useEffect(() => {
    loadAllUsers().catch(() => {
      // run() records the failure on the store; the screen renders it from
      // there. Nothing further to do here, but the promise must not float.
    });
  }, []);

  const onSearchChange = (value: string) => {
    setSearch(value);
    setFirst(0);
  };

  const filteredUsers = filterUsers(users, search);

  const renderBody = () => {
    if (error) {
      return (
        <p className={styles.emptyState} role="alert">
          {error}
        </p>
      );
    }
    if (isLoading) {
      return (
        <p className={styles.emptyState}>{TEXT.userManagement.loadingUsers}</p>
      );
    }
    if (filteredUsers.length === 0) {
      return (
        <p className={styles.emptyState}>
          {users.length === 0
            ? TEXT.userManagement.noUsersYet
            : TEXT.userManagement.noUsersMatchFilters}
        </p>
      );
    }
    return (
      <UsersTable
        first={first}
        users={filteredUsers}
        onEditUser={(user) => setModal({ mode: "edit", user })}
        onPageChange={setFirst}
      />
    );
  };

  return (
    <div className={styles.page}>
      <UserManagementHeader onInviteUser={() => setModal({ mode: "add" })} />
      <UserFilters
        resultCount={filteredUsers.length}
        search={search}
        onSearchChange={onSearchChange}
      />
      {renderBody()}
      {modal && (
        <UserFormModal
          mode={modal.mode}
          user={modal.mode === "edit" ? modal.user : undefined}
          onCancel={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reloadCurrent();
          }}
        />
      )}
    </div>
  );
};

export default UserManagementPage;
